/* eslint-disable array-bracket-newline */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAccount } from 'wagmi';
import { IFetchTickersPayload, ITicker } from '@kattana-io/common';
import Text from '../../atoms/Text';
import AlertsView from './AlertsView';
import WatchlistRowNotTicker from './WatchlistRowNotTicker';
import WatchlistRow from './WatchlistRow';
import useGetWatchlistItems from './useGetWatchlistItems';
import {
  actionAddWatchlist,
  actionRemoveWatchlist,
} from '../../actions/watchlist';
import { actionShowModal } from '../../actions/modal';
import { ModalTypes } from '../../reducers/modal';
import { IStore } from '../../reducers';
import { KattanaAPI } from '../../store/createStore';
import { switchCurrentTicker } from '../../Authlogic';

import './styles.scss';

interface IProps {
  visible: boolean;
}

export const watchlistSelector = (state: IStore) => ({
  current: state.current,
  watchlistItems: state.watchlist.tickers,
  trending_items: state.trendingReducer.items,
  trending_loading: state.trendingReducer.loading,
});

export const useTrendingItems = (trending_items: any[]) => {
  const [trending_items_ids, set_trending_items_ids] = useState<any[]>(
    trending_items.map((i) => i.ticker_id).filter((i) => i) // remove nulls
  );

  const [trending_items_no_ids, set_trending_items_no_ids] = useState<any[]>(
    trending_items.filter((i) => !i.ticker_id) // Only nulls
  );

  // update items if they are loaded after component is mounted
  useEffect(() => {
    set_trending_items_ids(
      trending_items.map((i) => i.ticker_id).filter((i) => i)
    );

    set_trending_items_no_ids(trending_items.filter((i) => !i.ticker_id));
  }, [trending_items.length]);

  return {
    trending_items_ids: trending_items_ids ?? [],
    trending_items_no_ids,
  };
};

const Watchlist = ({ visible }: IProps) => {
  const { current, watchlistItems, trending_items, trending_loading } =
    useSelector(watchlistSelector);

  const dispatch = useDispatch();
  const [searchedData, setSearchedData] = useState<ITicker[] | null>([]);
  const { address: account } = useAccount();
  const hasAccount = !!account;
  const [search, setSearch] = useState('');
  const { trending_items_ids, trending_items_no_ids } =
    useTrendingItems(trending_items);

  // trending tickers
  const trending = useGetWatchlistItems(trending_items_ids.join(','));

  const collection = useMemo(
    () =>
      watchlistItems?.filter((item) =>
        item?.name.toLocaleLowerCase().includes(search.toLocaleLowerCase())
      ),
    [watchlistItems, search]
  );

  const onAccountRequired = useCallback(
    () => dispatch(actionShowModal(ModalTypes.connect, {})),
    [dispatch]
  );

  const onSwitch = useCallback(
    (payload: ITicker) => switchCurrentTicker(payload),
    []
  );

  const onRemove = useCallback(
    (index: number) => dispatch(actionRemoveWatchlist(index)),
    [dispatch]
  );

  const onAdd = useCallback(
    (item: ITicker) => dispatch(actionAddWatchlist(item)),
    [dispatch]
  );

  const searchedTickets = async (controller: AbortController) => {
    const payload: IFetchTickersPayload = {
      searchRaw: search,
      sort: {
        name: 'liquidity',
        order: 'DESC',
      },
    };
    const data = await KattanaAPI.tickers(payload, {
      signal: controller.signal,
    });
    setSearchedData(data);
  };

  useEffect(() => {
    const controller = new AbortController();
    if (search) {
      searchedTickets(controller);
    } else {
      setSearchedData(null);
    }
    return () => controller.abort();
  }, [search]);

  const topThreeTickets = useMemo(() => {
    const excludes: any = [
      ...watchlistItems,
      current,
      ...trending.items,
    ].reduce((cons, el) => ({ ...cons, [el.id]: el.name }), {});

    return searchedData?.filter((item) => !excludes[item.id]).slice(0, 3);
  }, [searchedData, watchlistItems, trending.items, current]);

  const isCurrentPresent = [...trending.items, ...watchlistItems].some(
    (el) => el.id === current.id
  );

  return (
    <div className={`watchlist ${visible ? '' : 'hide'}`}>
      <div className='container'>
        <input
          type='search'
          className='search__input text--12 text--grey-600 text--weight-600'
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder='Search in Watchlist...'
        />
        <div className='items_container'>
          {!isCurrentPresent && (
            <WatchlistRow
              actionSwitchCurrent={onSwitch}
              actionAddWatchlist={onAdd}
              item={current}
              account={hasAccount}
              onAccountRequired={onAccountRequired}
              isFirst
              active
            />
          )}
          {trending_items_no_ids.map((item) => (
            <WatchlistRowNotTicker key={item.id} item={item} />
          ))}
          {trending.items.map((item, index) => (
            <WatchlistRow
              actionSwitchCurrent={onSwitch}
              key={item.id}
              item={item}
              active={current.id === item.id}
              account={hasAccount}
              onAccountRequired={onAccountRequired}
              isFirst={isCurrentPresent && index === 0}
              isTrending
            />
          ))}
          {collection.map((item, index) => (
            <WatchlistRow
              actionSwitchCurrent={onSwitch}
              actionRemoveWatchlist={onRemove}
              key={item.id}
              item={item}
              active={current.id === item.id}
              account={hasAccount}
              onAccountRequired={onAccountRequired}
              isFirst={
                isCurrentPresent && trending.items.length === 0 && index === 0
              }
            />
          ))}
        </div>
        {topThreeTickets && topThreeTickets?.length > 0 && (
          <div>
            <div className='watchlist__search_divider'>
              <Text
                size={14}
                className='watchlist__search_divider__search text--grey-400'
              >
                search
              </Text>
            </div>
            {topThreeTickets.map((item) => (
              <WatchlistRow
                key={item.id}
                actionSwitchCurrent={onSwitch}
                actionAddWatchlist={onAdd}
                item={item}
                active={current.id === item.id}
                account={hasAccount}
                onAccountRequired={onAccountRequired}
              />
            ))}
          </div>
        )}
      </div>
      <AlertsView />
    </div>
  );
};

export default Watchlist;
